/**
 * Copies text to the system clipboard. Awaits the async API, falls back to a
 * hidden-textarea + `document.execCommand('copy')` if it rejects, and returns
 * a boolean success indicator. Never throws.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  if (!text) return false;

  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to legacy fallback
    }
  }

  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.top = '-1000px';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  try {
    ta.select();
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(ta);
  }
}

/**
 * Copies text that is still being fetched — the Diagnose fix link only exists
 * after a POST. iOS Safari rejects a clipboard write made after the tap's user
 * activation has been spent awaiting the network, but accepts one started
 * inside the tap with a ClipboardItem holding a promise. Browsers without
 * ClipboardItem get an ordinary write once the text arrives. Never throws.
 */
export async function copyPromisedText(text: Promise<string>): Promise<boolean> {
  const Item = (globalThis as { ClipboardItem?: typeof ClipboardItem }).ClipboardItem;
  if (Item && navigator.clipboard?.write) {
    try {
      const blob = text.then((t) => new Blob([t], { type: 'text/plain' }));
      await navigator.clipboard.write([new Item({ 'text/plain': blob })]);
      return true;
    } catch {
      // fall through to a plain write once the text is known
    }
  }
  try {
    return await copyToClipboard(await text);
  } catch {
    return false;
  }
}
